// ---------------------------------------------------------------------------
// Media - one way to put a picture on the server and one way to show it.
//
// A product photograph, a combo picture, tomorrow a menu banner: the same
// call, with a KIND naming which family it belongs to. The server keeps the
// file and answers with the REFERENCE the row stores - a relative public
// address, never a path on this machine. `resolveMediaUrl` is the one place
// that turns a stored reference into something an <img> can load; today that
// is the reference itself, and the day the files live on a CDN it is where
// the host gets prefixed, without a row or a form changing.
// ---------------------------------------------------------------------------

export type MediaKind = 'product' | 'combo' | 'menu';

/// The image types the server keeps. Anything else is refused here, before
/// a byte is sent, and again on the server.
export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const IMAGE_ACCEPT = IMAGE_MIME_TYPES.join(',');

/// The server's cap - the same number, so the picker says exactly what the
/// server would have said, without the round trip.
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export type ImageFileProblem = 'type' | 'size';

/// Why a file cannot be sent, or null when it can. The server enforces the
/// same two rules; this only saves a doomed upload.
export function checkImageFile(file: File): ImageFileProblem | null {
  if (!(IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size > MAX_IMAGE_BYTES) return 'size';
  return null;
}

/// Uploads one image of a kind and returns the reference to store on the
/// row. Uses FormData rather than JSON, so the CSRF header must be added by
/// hand - the shared api client only does that for JSON requests.
export async function uploadImage(kind: MediaKind, file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);

  const csrf = document.cookie.match(/(?:^|;\s*)yaghi_csrf=([^;]*)/)?.[1];

  const response = await fetch(`/api/uploads/images/${kind}`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'x-csrf-token': decodeURIComponent(csrf) } : {},
    body: formData,
  });

  const body = (await response.json().catch(() => ({}))) as { url?: string; error?: { message?: string } };

  if (!response.ok || !body.url) {
    throw new Error(body.error?.message ?? 'The image could not be uploaded.');
  }

  return body.url;
}

/// The address an <img> loads for a stored reference.
///
/// Identity today: the app serves its own uploads. When the files move to an
/// object store or CDN, this is the ONE place that learns the host - every
/// picture in the till, the offers list and a future menu goes through it.
export function resolveMediaUrl(reference: string | null | undefined): string | null {
  if (!reference) return null;
  return reference;
}

/// The widths the server keeps a smaller copy at, named by convention beside
/// the original: `photo.png` has `photo-320.webp`, `photo-640.webp` and
/// `photo-1024.webp`. Defined in the backend's media.service.
const VARIANT_WIDTHS = [320, 640, 1024];

/// The `srcset` for a stored picture, so the browser downloads the size it is
/// about to draw instead of the photographer's original.
///
/// The shop's product photographs average 2.3 MB at 1450 pixels wide, and a
/// till tile draws them about 200 pixels across. This is the difference
/// between a grid of cards costing tens of megabytes and costing a few
/// hundred kilobytes.
///
/// Returns undefined for an absolute address (a CDN has its own sizes) and
/// for anything that is not a picture we make copies of, in which case the
/// `<img>` simply uses `src` as before. Safe even when a copy was never made:
/// the server answers a missing variant with the original rather than a 404.
export function mediaSrcSet(reference: string | null | undefined): string | undefined {
  if (!reference) return undefined;
  if (/^https?:\/\//i.test(reference) || reference.startsWith('data:')) return undefined;
  if (!/\.(png|jpe?g|webp)$/i.test(reference)) return undefined;
  const stem = reference.replace(/\.[^.]+$/, '');
  return VARIANT_WIDTHS.map((width) => `${resolveMediaUrl(`${stem}-${width}.webp`)} ${width}w`).join(', ');
}
